// Sibling group swatches — multi-dimension sibling navigation (color swatches + chips).
// Custom element: connectedCallback fires on every insertion path (PDP, quick-view modal,
// quick-view refresh, theme-editor re-renders) without requiring the <script> tag to re-execute.

const NAVIGABLE_SELECTOR = [
  ".product__color-swatch--sibling-product[href]",
  ".product__chip[href]:not(.selected)",
].join(", ");

const AVAILABILITY_SWATCH_SELECTOR =
  ".product__color-swatch[data-variant-availability]";

class SiblingGroupSwatches extends HTMLElement {
  // "Already initialized" guard — abort() on disconnect removes all listeners at once.
  #abort = null;
  #loaderTemplate = null;

  connectedCallback() {
    if (this.#abort) return;
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#loaderTemplate ??= this.querySelector(".loader")?.cloneNode(true);

    const config = this.#readConfig();
    if (!config) {
      this.#fail();
      return;
    }

    if (this.#isQuickView) {
      this.addEventListener("click", this.#onNavigate, { signal });
    }

    // dispatchCustomEvent (theme.js) doesn't set bubbles; size chips are outside our subtree.
    document.addEventListener("click", this.#onSizeChipClick, { signal });
    document.addEventListener("product:variant-change", this.#onVariantChange, {
      signal,
    });

    this.#load(config);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  get #isQuickView() {
    return !!this.closest("[data-is-quick-view]");
  }

  get #scope() {
    // Quick-view nodes have no .shopify-section ancestor — matches convention in product-swatch.liquid.
    return this.closest("[data-is-quick-view], .shopify-section") ?? document;
  }

  get #selectedSize() {
    return this.#scope.querySelector(
      "[data-option-buttons] .product__chip.selected",
    )?.dataset.optionValue;
  }

  #readConfig() {
    const dimensionsEl = this.querySelector(
      ':scope > script[type="application/json"][data-dimensions]',
    );
    if (!dimensionsEl) {
      console.error("sibling-group-swatches: missing dimensions JSON tag");
      return null;
    }

    const {
      collectionHandle,
      swatchStyle,
      siblingSwatchSource,
      currentProductId,
      chipLayout,
    } = this.dataset;

    return {
      collectionHandle,
      swatchStyle,
      siblingSwatchSource,
      chipLayout,
      currentProductId: parseInt(currentProductId, 10),
      dimensions: JSON.parse(dimensionsEl.textContent),
    };
  }

  async #load(config) {
    try {
      const siblingData = await fetchSiblingProducts(config.collectionHandle);

      // The modal may have closed (or moved to another product) while the fetch was in flight.
      if (this.#abort.signal.aborted || !this.isConnected) return;

      const currentProduct = siblingData?.products?.find(
        (product) => product.id === config.currentProductId,
      );
      if (!currentProduct) {
        this.#fail();
        return;
      }

      this.#render(config, siblingData, currentProduct);
    } catch (err) {
      console.error("sibling-group-swatches: unexpected error", err);
      this.#fail();
    }
  }

  #render(config, siblingData, currentProduct) {
    const { swatchStyle, siblingSwatchSource, chipLayout, dimensions } = config;
    const { swatchShape, products } = siblingData;

    const swatchOptions = {
      style: swatchStyle,
      shape: swatchShape,
      source: siblingSwatchSource,
      layout: chipLayout,
    };

    const blocks = dimensions.map((dimension, i) => {
      const filtered = filterByHigherDimensions(
        products,
        dimensions,
        i,
        currentProduct,
      );
      const currentValue = getValueForDimension(currentProduct, dimension.key);
      const valueMap = buildDimensionData(filtered, dimension, currentProduct);

      return this.#renderDimensionBlock(
        valueMap,
        dimension,
        currentValue,
        swatchOptions,
      );
    });

    this.replaceChildren(...blocks);

    this.#syncAvailability();
    if (typeof updateSwatchStrikethroughColors === "function") {
      updateSwatchStrikethroughColors(this);
    }
  }

  #syncAvailability(size = this.#selectedSize) {
    if (!size) return;
    this.querySelectorAll(AVAILABILITY_SWATCH_SELECTOR).forEach((swatch) => {
      const availability = JSON.parse(swatch.dataset.variantAvailability);
      swatch.classList.toggle("unavailable", availability[size] === false);
    });
  }

  #showLoader() {
    if (!this.#loaderTemplate) return;
    this.replaceChildren(this.#loaderTemplate.cloneNode(true));
  }

  #fail() {
    this.replaceChildren();
    this.hidden = true;
  }

  #onSizeChipClick = (e) => {
    const chip = e.target.closest("[data-option-buttons] .product__chip");
    if (!chip || !this.#scope.contains(chip)) return;
    this.#syncAvailability(chip.dataset.optionValue);
  };

  #onVariantChange = () => {
    this.#syncAvailability();
  };

  #onNavigate = (e) => {
    const link = e.target.closest(NAVIGABLE_SELECTOR);
    if (!link || !this.contains(link)) return;

    // stopPropagation required: pageTransition (theme.js) delegates a[href] clicks on body in bubble phase.
    e.preventDefault();
    e.stopPropagation();

    this.#showLoader();
    window.flu.events.emit("quick-view:refresh", null, {
      productUrl: link.pathname,
    });
  };

  #renderDimensionBlock(valueMap, dimension, currentValue, swatchOptions) {
    const block = document.createElement("div");
    block.classList.add("product__block", "product__block--medium");

    const labelWrapper = document.createElement("div");
    labelWrapper.className = "product__label-wrapper";

    const label = document.createElement("span");
    label.classList.add("product__label", "fs-body-100");
    label.textContent = dimension.label;

    const labelValueEl = document.createElement("span");
    labelValueEl.className = "t-opacity-70";
    labelValueEl.textContent = currentValue;
    label.appendChild(document.createTextNode(": "));
    label.appendChild(labelValueEl);

    labelWrapper.appendChild(label);

    const itemsWrapper =
      dimension.key === "color"
        ? this.#renderColorSwatches(
            valueMap,
            currentValue,
            swatchOptions,
            labelValueEl,
          )
        : this.#renderChips(valueMap, currentValue, swatchOptions.layout);

    block.appendChild(labelWrapper);
    block.appendChild(itemsWrapper);
    return block;
  }

  #renderColorSwatches(valueMap, currentValue, swatchOptions, labelValueEl) {
    const itemsWrapper = document.createElement("div");
    itemsWrapper.className = "product__color-swatches";
    itemsWrapper.style.cssText = swatchOptions.style;
    if (swatchOptions.layout) itemsWrapper.dataset.layout = swatchOptions.layout;

    const inner = document.createElement("div");
    inner.className = "product__color-swatches--inner";

    for (const [
      val,
      {
        url,
        swatchColor,
        featuredMediaUrl,
        customSwatchImageUrl,
        onSale,
        variantAvailability,
      },
    ] of valueMap) {
      const isSelected = val === currentValue;
      const el = isSelected
        ? document.createElement("span")
        : document.createElement("a");
      el.classList.add(
        "product__color-swatch",
        "product__color-swatch--sibling-product",
      );
      if (isSelected) el.classList.add("selected");
      if (onSale) el.classList.add("on_sale");
      el.style.backgroundColor = swatchColor;
      el.dataset.shape = swatchOptions.shape;
      el.dataset.siblingCutline = val;
      el.dataset.variantAvailability = JSON.stringify(variantAvailability);
      el.setAttribute("aria-label", val);
      if (!isSelected) el.href = url;
      const imageSrc =
        swatchOptions.source === "image" && featuredMediaUrl
          ? featuredMediaUrl
          : customSwatchImageUrl;
      if (imageSrc) {
        const img = document.createElement("img");
        img.src = imageSrc;
        img.alt = val;
        img.loading = "lazy";
        img.setAttribute("role", "presentation");
        el.appendChild(img);
      }
      el.addEventListener("mouseover", () => {
        labelValueEl.textContent = val;
      });
      el.addEventListener("mouseout", () => {
        labelValueEl.textContent = currentValue;
      });
      inner.appendChild(el);
    }

    itemsWrapper.appendChild(inner);
    return itemsWrapper;
  }

  #renderChips(valueMap, currentValue, layout) {
    const itemsWrapper = document.createElement("div");
    itemsWrapper.classList.add("product__color-chips");
    itemsWrapper.dataset.layout = layout || "natural";

    for (const [val, { url }] of valueMap) {
      const isSelected = val === currentValue;
      if (isSelected) {
        const chip = document.createElement("span");
        chip.classList.add("product__chip", "selected");
        chip.textContent = val;
        itemsWrapper.appendChild(chip);
      } else {
        const chip = document.createElement("a");
        chip.className = "product__chip";
        chip.href = url;
        chip.textContent = val;
        itemsWrapper.appendChild(chip);
      }
    }

    return itemsWrapper;
  }
}

if (!customElements.get("sibling-group-swatches")) {
  customElements.define("sibling-group-swatches", SiblingGroupSwatches);
}

function buildDimensionData(products, dimension, currentProduct) {
  const valueMap = new Map();

  for (const option of dimension.options || []) {
    if (option) valueMap.set(option, null);
  }

  for (const product of products) {
    const dimensionValue = getValueForDimension(product, dimension.key);

    if (!dimensionValue) continue;
    if (dimension.options?.length && !valueMap.has(dimensionValue)) continue;

    const existing = valueMap.get(dimensionValue);

    if (existing && product.id !== currentProduct.id) continue;

    // Skip products without color — not fully configured for sibling navigation
    if (!getValueForDimension(product, "color")) continue;

    valueMap.set(dimensionValue, {
      url: product.url,
      swatchColor: product.swatch_style,
      featuredMediaUrl: product.featured_media_url,
      customSwatchImageUrl: product.custom_swatch_image_url,
      onSale: isProductOnSale(product),
      variantAvailability: product.variant_availability,
    });
  }

  for (const [key, val] of valueMap) {
    if (val === null) valueMap.delete(key);
  }

  return valueMap;
}

function filterByHigherDimensions(
  products,
  dimensions,
  upToIndex,
  currentProduct,
) {
  if (upToIndex === 0) return products;

  const requiredDimensions = dimensions.slice(0, upToIndex);
  return products.filter((product) =>
    requiredDimensions.every(
      (dim) =>
        product.metafields[dim.key] === currentProduct.metafields[dim.key],
    ),
  );
}

function getValueForDimension(product, dimensionKey) {
  return product.metafields[dimensionKey];
}

function isProductOnSale(product) {
  return (
    product.compare_at_price !== null &&
    product.compare_at_price > product.price
  );
}

async function fetchSiblingProducts(collectionHandle) {
  const res = await fetch(
    `/collections/${collectionHandle}?sections=sibling-group-data`,
  );
  if (!res.ok) {
    console.error(
      `sibling-group-swatches: fetch failed for "${collectionHandle}" (${res.status})`,
    );
    return null;
  }
  const data = await res.json();
  const doc = new DOMParser().parseFromString(
    data["sibling-group-data"],
    "text/html",
  );
  const productsEl = doc.getElementById("sibling-group-products-json");
  if (!productsEl) {
    console.error(
      `sibling-group-swatches: sibling-group-data section missing JSON tag`,
    );
    return null;
  }
  const { swatch_shape: swatchShape, products } = JSON.parse(
    productsEl.textContent,
  );
  return { swatchShape, products };
}
